export type NamespacedTranslationsInstance = Readonly<
    ReturnType<typeof createTranslationsInstance>
>;

export type PluralizationResolver = (n: number, numberOfAvailableTranslations: number) => number;

export function createTranslationsInstance(
    initialTranslations: Record<string, string> = {},
    pluralizationResolver: PluralizationResolver = defaultPluralizationResolver,
) {
    let translations: Record<string, string> = initialTranslations ?? {};

    function setTranslations(externalTranslations: Record<string, string>): void {
        translations = externalTranslations;
    }

    function translate(key: string, ...args: unknown[]): string {
        const rawTranslation = translations[key] ?? '';

        const htmlLabel = _toHtml(rawTranslation);

        if (args.length === 0) {
            return htmlLabel;
        } else {
            return interpolate(htmlLabel, ...args);
        }
    }

    // TODO: Might require additional checks as we start using this more
    function translatePluralized(key: string, choice: number, ...args: unknown[]): string {
        const rawTranslations = translations[key]
            ? translations[key].split(/\s*\|\s*/g).filter((it) => !!it?.trim())
            : [];
        const numberOfAvailableTranslations = rawTranslations.length;
        const pluralizationIndex = numberOfAvailableTranslations > 1
            ? pluralizationResolver(choice, numberOfAvailableTranslations)
            : 0;

        const rawTranslation = rawTranslations[pluralizationIndex] ?? '';

        const htmlLabel = _toHtml(rawTranslation);
        const allArgs = [choice, ...args];

        if (allArgs.length === 0) {
            return htmlLabel;
        } else {
            return interpolate(htmlLabel, ...allArgs);
        }
    }

    return {
        setTranslations,
        translate,
        translatePluralized,
    };
}

// Exported for testing only
export function _toHtml(translation: string): string {
    const withReplacedSymbols = translation
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const withReplacedMarkdown = withReplacedSymbols
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/_(.*?)_/g, '<em>$1</em>');

    const withReplacedLists = _replaceRawTextDotsWithHtmlLists(withReplacedMarkdown);

    return _replaceParagraphs(withReplacedLists);
}

/**
 * Transforms raw translation text with dots into HTML lists.
 * For example:
 *
 * ```
 * This is my list:
 *
 * • Item 1
 * • Item 2
 *
 * And also
 *
 * • List Item 1
 * • List Item 2
 * ```
 *
 * becomes:
 *
 * ```
 * This is my list:
 *
 * <ul>
 *   <li>Item 1</li>
 *   <li>Item 2</li>
 * </ul>
 *
 * And also
 *
 * <ul>
 *   <li>List Item 1</li>
 *   <li>List Item 2</li>
 * </ul>
 * ```
 * @param translation Raw translation text
 * @returns HTML string where <ul> and <li> tags are used
 */
export function _replaceRawTextDotsWithHtmlLists(translation: string): string {
    const withListItems = translation.replace(/^[ \t]*•\s*(.+)/gmu, '<ul>\n\t<li>$1</li>\n</ul>');

    return withListItems.replace(/\s*<\/ul>\n<ul>/gmu, '');
}

/**
 * Replaces blocks of text separated by at least two new lines with paragraph blocks.
 * Text which has no such blocks of two+ new lines is not changed.
 *
 * Additionally, anything that is a list (<ul>) is _not_ wrapped in <p> tags.
 *
 * Example:
 *
 * ```
 * This is some text, and I
 * really love it.
 *
 * This is other text.
 * ```
 *
 * becomes:
 *
 * ```
 * <p>
 * This is some text, and I
 * really love it.
 * </p>
 * <p>
 * This is other text.
 * </p>
 * ```
 * @param translation Raw translation text
 * @returns HTML string where <p> tags are used
 */
export function _replaceParagraphs(translation: string): string {
    if (!/\n\n+/.test(translation)) {
        return translation;
    }

    const withParagraphs = translation
        .replace(/(\n\s*\n)/g, '</p>\n<p>')
        .replace(/^\s*<\/p>\n<p>/gm, '<p>');

    return (
        `<p>${withParagraphs}</p>`
            // Do not wrap lists in <p> tags (or, rather, undo it)
            .replace(/<p><ul>/g, '<ul>')
            .replace(/<\/ul><\/p>/g, '</ul>')
    );
}

function interpolate(str: string, ...args: unknown[]): string {
    let currentIndex = 0;
    return str
        .replace(/%%/g, '\\%')
        .replace(/(?<![\\\d])%(?:(\d+)\$)?(s|d|(?:\.\d+)?f)?/g, (_, stringIndex, specifier): string => {
            const rawValue = stringIndex
                ? (args[parseInt(stringIndex ?? '1', 10) - 1] as string) ?? ''
                // eslint-disable-next-line no-plusplus
                : args[currentIndex++]?.toString() ?? '';

            return formatBySpecifier(rawValue, specifier);
        }).replace(/\\%/g, '%');
}

function formatBySpecifier(rawValue: string, specifier?: string): string {
    if (specifier == null || specifier === 's') {
        return rawValue;
    } else if (specifier === 'd') {
        return parseInt(rawValue, 10).toString();
    } else if (specifier.startsWith('.') && specifier.endsWith('f')) {
        const precision = parseInt(specifier.substring(1), 10);
        const value = parseFloat(rawValue);
        return value.toFixed(precision);
    } else if (specifier === 'f') {
        const value = parseFloat(rawValue);
        return value.toString();
    } else {
        console.warn(`Unknown specifier "${specifier}"`);
        return rawValue;
    }
}

function defaultPluralizationResolver(n: number, numberOfAvailableTranslations: number): number {
    if (numberOfAvailableTranslations === 1) {
        return 0;
    } else if (numberOfAvailableTranslations === 2) {
        const isOne = n % 10 === 1 && n % 100 !== 11;

        return isOne ? 0 : 1;
    } else if (numberOfAvailableTranslations === 3) {
        const isOne = n % 10 === 1 && n % 100 !== 11;
        const isZero = n === 0;

        if (isZero) {
            return 0;
        } else if (isOne) {
            return 1;
        } else {
            return 2;
        }
    } else {
        console.warn('Pluralization resolver not implemented for more than 3 translations');
        return 0;
    }
}
